import { useSelectedClient } from '@entities/clients/heplers.ts';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMobile } from '@shared/hooks/index.ts';
import { usePartitionsQuery } from '@src/api/api-hooks/common/usePartitionsQuery.ts';
import { useTagsQuery } from '@src/api/api-hooks/common/useTagsQuery.ts';
import { useAuthorsQuery } from '@src/api/api-hooks/users/useAuthorsQuery.ts';

export const useFilter = (onStateChange?: () => void) => {
  const [searchNotes, setSearchNotes] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [selectedPartitions, setSelectedPartitions] = useState<number[]>([]);
  const [selectedAuthors, setSelectedAuthors] = useState<number[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState<string>(searchParams.get('search') || '');
  const { clearSelectedClient } = useSelectedClient();
  const [isSearchMobile, setIsSearchMobile] = useState(search !== '');
  const { isMobile } = useMobile();

  const { data: partitions } = usePartitionsQuery();
  const { data: authors } = useAuthorsQuery();
  const { data: tags } = useTagsQuery({ partition: selectedPartitions?.at(0) });

  useEffect(() => {
    const filterTags = searchParams.get('tags');
    const filterAuthors = searchParams.get('authors');
    const filterSearch = searchParams.get('search');
    const filterModules = searchParams.get('partitions');

    if (filterModules) {
      setSelectedPartitions(filterModules.split(',').map((id) => Number(id)));
    }
    if (filterTags) {
      setSelectedTags(filterTags.split(',').map((id) => Number(id)));
    }
    if (filterAuthors) {
      setSelectedAuthors(filterAuthors.split(',').map((id) => Number(id)));
    }
    if (filterSearch) {
      isMobile && setIsSearchMobile(true);
    }
  }, []);

  const onSearch = (search: string) => {
    onStateChange && onStateChange();
    setSearch(search);
    clearSelectedClient();
    if (search.length) {
      searchParams.set('search', search);
      setSearchParams(searchParams);
    } else {
      searchParams.delete('search');
      setSearchParams(searchParams);
    }
  };
  const onSearchNotes = (searchNotes: string) => setSearchNotes(searchNotes);

  const onTagChange = (tags: number[]) => {
    onStateChange && onStateChange();
    setSelectedTags(tags);
    if (tags.length) {
      searchParams.set('tags', tags.join(','));
      setSearchParams(searchParams);
    } else {
      searchParams.delete('tags');
      setSearchParams(searchParams);
    }
  };
  const onPartitionsChange = (partitions: number[]) => {
    onStateChange && onStateChange();
    setSelectedPartitions(partitions);
    if (partitions.length) {
      if (partitions?.at(0) === selectedPartitions?.at(0)) {
        setSelectedPartitions([]);
        searchParams.delete('partitions');
        setSearchParams(searchParams);
      } else {
        searchParams.set('partitions', partitions.join(','));
        setSearchParams(searchParams);
      }
    } else {
      setSelectedPartitions([]);
      searchParams.delete('partitions');
      setSearchParams(searchParams);
    }
  };

  const onAuthorChange = (authors: number[]) => {
    onStateChange && onStateChange();
    setSelectedAuthors(authors);
    if (authors.length) {
      searchParams.set('authors', authors.join(','));
      setSearchParams(searchParams);
    } else {
      searchParams.delete('authors');
      setSearchParams(searchParams);
    }
  };

  const handleResetFilter = () => {
    onTagChange([]);
    onPartitionsChange([]);
    onAuthorChange([]);
  };

  const isFilterActive = useMemo(
    () => Boolean(selectedTags.length || selectedAuthors.length || selectedPartitions.length),
    [selectedTags, selectedAuthors, selectedPartitions]
  );

  const [showFilterBar, setShowFilterBar] = useState(() => {
    if (isMobile) {
      return false;
    }
    return isFilterActive;
  });

  return {
    search,
    onSearch,
    searchNotes,
    onSearchNotes,
    selectedTags,
    selectedAuthors,
    onAuthorChange,
    onTagChange,
    isSearchMobile,
    setIsSearchMobile,
    onPartitionsChange,
    selectedPartitions,
    handleResetFilter,
    isFilterActive,
    partitions,
    tags,
    authors,
    showFilterBar,
    setShowFilterBar,
  };
};
